/**
 * Decisions — route: /brief
 *
 * Three groups derived from the rows and the viewer (lib/briefGroups.ts):
 * Needs you · Waiting on others · Decided. One search box. Each row carries
 * the same one-sentence status and the same one button the decision page
 * shows (lib/briefNextAction.ts). Replaces the Decision Log's three view
 * modes, three-facet filter panel and stats bar.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { listBriefsWithProgress, getWorkspaceBriefCount, type BriefProgress } from '../../services/brief/briefService';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { canCreateBrief, resolveBriefTier, FREE_BRIEF_LIMIT } from '../../lib/entitlements';
import { DECISION_TYPE_LABELS, DECISION_SCOPE_LABELS } from '../../types/brief';
import { computeBriefNextAction, type BriefNextAction } from './lib/briefNextAction';
import { groupBriefs, matchesSearch, BRIEF_GROUP_LABELS, type BriefGroupId, type BriefListRow } from './lib/briefGroups';
import { btnPrimary } from '@/components/mono/primitives';

interface Row extends BriefListRow {
  next: BriefNextAction;
}

function DecisionRow({ row }: { row: Row }) {
  const navigate = useNavigate();
  const { brief, next } = row;
  const isLive = brief.status !== 'decided' && brief.status !== 'archived';
  return (
    <li className="border border-[var(--rule)] bg-[var(--bg-raised)]">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
        <Link to={`/brief/${brief.id}`} className="flex-1 min-w-0 group">
          <p className="text-base font-medium text-[var(--text-pri)] group-hover:underline truncate">{brief.title}</p>
          <p className="text-sm text-[var(--text-sec)] mt-0.5">
            {isLive ? (
              <>
                The ball is with <span className="text-[var(--text-pri)]">{next.ballWith}</span>
                <span className="text-[var(--text-dim)]"> · </span>
              </>
            ) : null}
            {next.status}
          </p>
          {(brief.decision_type || brief.decision_scope || brief.scope_label) && (
            <p className="text-xs text-[var(--text-dim)] mt-1">
              {[brief.decision_type ? DECISION_TYPE_LABELS[brief.decision_type] : null, brief.decision_scope ? DECISION_SCOPE_LABELS[brief.decision_scope] : null, brief.scope_label]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </Link>
        {next.primary ? (
          <button
            type="button"
            onClick={() => navigate(`/brief/${brief.id}`)}
            className="flex-shrink-0 inline-flex items-center h-10 px-4 bg-[var(--slate)] text-white font-mono text-xs uppercase tracking-[0.08em] hover:opacity-90"
          >
            {next.primary.label} →
          </button>
        ) : (
          <Link to={`/brief/${brief.id}`} className="flex-shrink-0 inline-flex items-center h-10 px-4 border border-[var(--rule-bright)] text-[var(--text-sec)] font-mono text-xs uppercase tracking-[0.08em] hover:bg-[var(--hover-tint)]">
            Open
          </Link>
        )}
      </div>
    </li>
  );
}

function Group({ id, rows, collapsedByDefault }: { id: BriefGroupId; rows: Row[]; collapsedByDefault?: boolean }) {
  const [open, setOpen] = useState(!collapsedByDefault);
  if (rows.length === 0) return null;
  return (
    <section aria-labelledby={`group-${id}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 id={`group-${id}`} className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--text-ter)]">
          {BRIEF_GROUP_LABELS[id]} · {rows.length}
        </h2>
        {collapsedByDefault && (
          <button type="button" onClick={() => setOpen((v) => !v)} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)]">
            {open ? 'Hide' : `Show ${rows.length}`}
          </button>
        )}
      </div>
      {open && (
        <ul className="space-y-2">
          {rows.map((r) => (
            <DecisionRow key={r.brief.id} row={r} />
          ))}
        </ul>
      )}
    </section>
  );
}

export default function DecisionsListPage() {
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspacePlan = useTenantStore((s) => s.workspacePlan);
  const userId = useUserStore((s) => s.userId);

  const [progress, setProgress] = useState<BriefProgress[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [limitNotice, setLimitNotice] = useState(false);

  const isPro = workspacePlan ? resolveBriefTier(workspacePlan) === 'brief_pro' : false;
  const canCreate = workspacePlan ? canCreateBrief(workspacePlan, activeCount) : true;

  useEffect(() => {
    if (!currentWorkspaceId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([listBriefsWithProgress(currentWorkspaceId), getWorkspaceBriefCount(currentWorkspaceId)])
      .then(([rows, count]) => {
        if (cancelled) return;
        setProgress(rows);
        setActiveCount(count);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load your decisions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspaceId]);

  const groups = useMemo(() => {
    const rows: Row[] = progress
      .filter((p) => matchesSearch(p.brief, search))
      .map((p) => {
        const next = computeBriefNextAction({ brief: p.brief, options: p.options, criteriaCount: p.criteriaCount, decision: p.decision, viewerId: userId });
        return {
          brief: p.brief,
          optionCount: p.options.length,
          submittedCount: p.options.filter((o) => o.proposal).length,
          criteriaCount: p.criteriaCount,
          needsViewer: next.needsViewer || (next.primary !== null && p.brief.status === 'draft'),
          next,
        };
      });
    return groupBriefs(rows) as { needs_you: Row[]; waiting: Row[]; decided: Row[] };
  }, [progress, search, userId]);

  const total = progress.length;
  const shown = groups.needs_you.length + groups.waiting.length + groups.decided.length;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="border-b border-[var(--rule)] bg-[var(--bg-raised)]">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--text-ter)]">Priorities</p>
              <h1 className="font-heading font-semibold text-2xl text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>Decisions</h1>
            </div>
            {canCreate ? (
              <Link to="/brief/new" {...btnPrimary()}>
                <PlusIcon className="w-4 h-4" /> New decision
              </Link>
            ) : (
              <button type="button" onClick={() => setLimitNotice(true)} {...btnPrimary()}>
                <PlusIcon className="w-4 h-4" /> New decision
              </button>
            )}
          </div>
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-[var(--text-dim)] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search decisions"
              aria-label="Search decisions"
              className="w-full h-10 pl-9 pr-3 text-base border border-[var(--rule)] bg-[var(--surface)] text-[var(--text-pri)] placeholder:text-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-[var(--slate)]"
            />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {!isPro && workspacePlan && (
          <p className="text-sm text-[var(--text-ter)]">
            Free plan: {activeCount} of {FREE_BRIEF_LIMIT} active decisions.{' '}
            <a href="mailto:hello@priorities.ai?subject=Decision Brief Pro" className="underline">Upgrade for unlimited.</a>
          </p>
        )}
        {limitNotice && !canCreate && (
          <div role="alert" className="border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-4 py-3 text-sm text-[var(--coral)] flex items-start justify-between gap-4">
            <span>You have used all {FREE_BRIEF_LIMIT} decisions on the free plan. Archive a decided one to make room, or upgrade for unlimited decisions.</span>
            <button type="button" onClick={() => setLimitNotice(false)} className="text-xs underline flex-shrink-0">Dismiss</button>
          </div>
        )}
        {error && (
          <div role="alert" className="border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-4 py-3 text-sm text-[var(--coral)]">{error}</div>
        )}

        {loading && (
          <div className="space-y-2" aria-busy="true">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-[var(--rule)] p-4 animate-pulse">
                <div className="h-4 bg-[var(--surface)] w-2/3 mb-2" />
                <div className="h-3 bg-[var(--surface)] w-1/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && total === 0 && (
          <div className="text-center py-16 px-6">
            <h2 className="font-heading font-semibold text-lg text-[var(--text-pri)] mb-2" style={{ color: 'var(--text-pri)' }}>No decisions yet</h2>
            <p className="text-base text-[var(--text-sec)] max-w-md mx-auto mb-6">
              A decision is a question with a deadline. Name it, add the options, ask the owners for their proposals, compare, decide — and the record is a link anyone can read.
            </p>
            <Link to="/brief/new" {...btnPrimary()}>
              <PlusIcon className="w-4 h-4" /> Start your first decision
            </Link>
          </div>
        )}

        {!loading && !error && total > 0 && shown === 0 && (
          <p className="text-base text-[var(--text-sec)] text-center py-10">Nothing matches "{search}".</p>
        )}

        {!loading && !error && shown > 0 && (
          <>
            <Group id="needs_you" rows={groups.needs_you} />
            <Group id="waiting" rows={groups.waiting} />
            <Group id="decided" rows={groups.decided} collapsedByDefault={groups.needs_you.length + groups.waiting.length > 0} />
          </>
        )}
      </div>
    </div>
  );
}
